import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { joyApi } from "../api/joyApi";
import AppShell from "../components/AppShell";
import LoadingScreen from "../components/LoadingScreen";
import ErrorState from "../components/ErrorState";

const TOTAL_LESSONS = 15;

function Stat({ emoji, value, label, testId }) {
  return (
    <div data-testid={testId} className="flex-1 flex flex-col items-center bg-white rounded-[18px] py-2.5 px-1 border-2 border-[#FFE9C7]">
      <span className="text-xl leading-none">{emoji}</span>
      <span className="font-black text-[#4A3728] text-lg leading-tight mt-1">{value}</span>
      <span className="text-[10px] font-bold text-[#8B7355] uppercase tracking-wide">{label}</span>
    </div>
  );
}

function BigButton({ emoji, title, subtitle, color, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="joy-press w-full flex items-center gap-4 rounded-[24px] px-5 py-4 text-left"
      style={{ background: color, boxShadow: "0 8px 0 rgba(0,0,0,0.08), 0 12px 22px rgba(0,0,0,0.08)" }}
    >
      <span className="text-3xl">{emoji}</span>
      <span className="flex flex-col">
        <span className="font-black text-white text-xl leading-tight drop-shadow">{title}</span>
        <span className="font-bold text-white/90 text-sm">{subtitle}</span>
      </span>
    </button>
  );
}

export default function Home() {
  const nav = useNavigate();
  const { userId } = useAuth();
  const { data: progress, isLoading, isError, refetch } = useQuery({
    queryKey: ["progress", userId],
    queryFn: () => joyApi.progress(userId),
    enabled: !!userId,
  });

  if (isLoading) return <LoadingScreen />;

  const completed = progress?.completed_lessons?.length || 0;
  const pct = Math.round((completed / TOTAL_LESSONS) * 100);

  return (
    <AppShell>
      {isError ? (
        <ErrorState onRetry={refetch} />
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <div
              data-testid="level-badge"
              className="flex items-center gap-1.5 bg-[#FFD700] rounded-full px-4 py-2 font-black text-[#4A3728]"
              style={{ boxShadow: "0 4px 10px rgba(255,215,0,0.4)" }}
            >
              🏆 Nível {progress?.current_level || 1}
            </div>
            <button
              data-testid="settings-button"
              onClick={() => nav("/settings")}
              className="joy-press w-11 h-11 rounded-full bg-white flex items-center justify-center text-xl border-2 border-[#FFC0CB]"
              aria-label="Configurações"
            >
              ⚙️
            </button>
          </div>

          <div className="flex flex-col items-center text-center joy-slide-in">
            <div className="relative joy-bob">
              <img
                src="/joy.png"
                alt="Joy"
                className="w-28 h-28 rounded-full object-cover border-4 border-[#FF69B4]"
                style={{ boxShadow: "0 12px 28px rgba(255,105,180,0.4)" }}
              />
              <span className="absolute -top-1 -right-1 text-xl joy-twinkle">✨</span>
            </div>
            <h1 className="text-2xl font-black text-[#FF69B4] mt-4 leading-tight">Joy's English Adventure</h1>
            <p className="text-xl font-extrabold text-[#4A3728] mt-2">Olá, amiguinho! 💕</p>
            <p className="text-[#8B7355] font-bold">Vamos aprender inglês com a Joy? ✨</p>
          </div>

          <div data-testid="stats-bar" className="flex gap-2 mt-5">
            <Stat emoji="🔥" value={progress?.streak_days || 0} label="Dias" testId="stat-streak" />
            <Stat emoji="⭐" value={progress?.total_xp || 0} label="XP" testId="stat-xp" />
            <Stat emoji="💎" value={progress?.coins || 0} label="Moedas" testId="stat-coins" />
            <Stat emoji="🎀" value={progress?.current_level || 1} label="Nível" testId="stat-level" />
          </div>

          <div className="flex flex-col gap-3 mt-6">
            <BigButton emoji="📚" title="Lições" subtitle="Aprenda com a Joy!" color="#FF69B4" onClick={() => nav("/lessons")} testId="nav-lessons" />
            <BigButton emoji="🌟" title="Progresso" subtitle="Suas conquistas!" color="#FFA500" onClick={() => nav("/progress")} testId="nav-progress" />
            <BigButton emoji="🏆" title="Ranking" subtitle="Veja os melhores jogadores!" color="#5B8FB9" onClick={() => nav("/leaderboard")} testId="nav-leaderboard" />
          </div>

          <div
            data-testid="daily-challenge"
            className="mt-6 rounded-[24px] p-5 text-center"
            style={{ background: "linear-gradient(135deg,#40E0D0,#87CEEB)" }}
          >
            <p className="font-black text-white text-lg drop-shadow">🎯 Desafio do Dia! 🌈</p>
            <p className="font-bold text-white/95 text-sm mt-1">Complete uma lição e ganhe 20 XP extras!</p>
          </div>

          <div className="mt-5 bg-white rounded-[24px] p-5 border-2 border-[#FFE9C7]">
            <p data-testid="lessons-completed" className="font-extrabold text-[#4A3728] mb-2">
              📖 Lições completadas: {completed} / {TOTAL_LESSONS} 💖
            </p>
            <div className="h-4 rounded-full bg-[#FFE0EC] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, background: "linear-gradient(90deg,#FF69B4,#FFD700)" }}
              />
            </div>
          </div>

          <p className="text-center text-2xl mt-6 tracking-widest">💕 ⭐ 🌟 💛 ✨</p>
        </>
      )}
    </AppShell>
  );
}
